import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import type {VNode} from 'snabbdom';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash/debounce';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {SidenavEl, SidenavJsxProps} from 'common/components/sidenav';
import {SidenavFooter, SidenavHeader, SidenavSection} from 'common/components/sidenav/types';
import {useStoreAndFetchOnce} from 'common/util/component-helpers';
import {userDetailsStore} from 'common/report/stores/user-details';
import {SIDENAV_COLLAPSED_WIDTH} from 'common/components/sidenav/constants';
import {layoutManager} from 'common/report/util/layout-manager';
import {isTestEnv} from 'common/report/util/env';
import {ContentFrame, MIN_PAGE_WIDTH} from 'common/widgets/sidenav-layout/content-frame';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getDownloadMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {panel2react} from 'common/util/react';

import css from './index.inline.styl';

type SidenavLayoutState = {
  sidenavWidth: number;
  maxSidenavWidth: number;
};

type SidenavLayoutJsxProps = {
  attrs?: {
    'hide-nav'?: boolean;
    'selected-nav-item'?: string;
    'no-padding'?: boolean;
    'is-sidenav-size-fixed'?: boolean;
    loaded?: boolean;
    'storage-key'?: string;
  };
  props?: {
    navHeader?: null | SidenavHeader;
    navSections?: SidenavSection[];
    footer?: SidenavFooter;
    confirmNavigateFunc?: () => boolean;
  };
  on?: Partial<
    Pick<SidenavJsxProps['on'], 'selectItem' | 'headerAction' | 'footerAction' | 'dragDrop' | 'overflowMenuSelect'> & {
      contentFrameResized: (ev: CustomEvent<{padding: number}>) => void;
    }
  >;
};

export class SidenavLayout extends Component<SidenavLayoutState, EmptyObj, EmptyObj, EmptyObj, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<SidenavLayoutJsxProps['attrs']> {
    return {
      'hide-nav': {
        type: `boolean`,
        description: `This hides the nav area entirely (different from collapsing), useful for views where some modes do not need a nav`,
      },
      'selected-nav-item': {type: `string`, description: `value of selected item`},
      loaded: {type: `boolean`},
      'is-sidenav-size-fixed': {type: `boolean`},
      'no-padding': {
        type: `boolean`,
        description: `This value ensures that there is no additional margins or padding on the elements inside SidenavLayout`,
      },
      'storage-key': {type: `string`, default: `default`},
    };
  }

  private selfResizeObserver: ResizeObserver | null = null;
  private selfWidth = 0;
  private userId = -1;
  private hasInitializedRequiredNavData = false;

  private paddingFrameResizeObserver: ResizeObserver | null = null;

  public get config(): ConfigOptions<SidenavLayoutState, EmptyObj, IronContextRegistry> {
    return {
      contexts: [`urlManager` as const],
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultState: {
        sidenavWidth: 0,
        maxSidenavWidth: 0,
      },
    };
  }

  private _navHeader: SidenavHeader | null = null;
  public set navHeader(value: SidenavHeader | null) {
    this._navHeader = value;
    this.initRequiredNavData();
    this.update();
  }
  public get navHeader(): SidenavHeader | null {
    return this._navHeader;
  }

  private _navSections: SidenavSection[] = [];
  public set navSections(value: SidenavSection[]) {
    this._navSections = value;
    this.initRequiredNavData();
    this.update();
  }
  public get navSections(): SidenavSection[] {
    return this._navSections;
  }

  private _footer: SidenavFooter = null;
  public set footer(value: SidenavFooter) {
    this._footer = value;
    this.initRequiredNavData();
    this.update();
  }
  public get footer(): SidenavFooter {
    return this._footer;
  }

  private _confirmNavigateFunc?: () => boolean;
  public set confirmNavigateFunc(value: () => boolean) {
    this._confirmNavigateFunc = value;
    this.update();
  }
  public get confirmNavigateFunc(): () => boolean {
    return this._confirmNavigateFunc;
  }

  public connectedCallback() {
    const debouncedUpdateLayout = debounce(this.updateLayout.bind(this), 100, {trailing: true});
    this.selfResizeObserver = new ResizeObserver((entries) => {
      this.selfWidth = entries[0].contentRect.width;
      debouncedUpdateLayout();
    });

    super.connectedCallback();

    this.selfResizeObserver.observe(this);
  }

  public disconnectedCallback() {
    this.selfResizeObserver.disconnect();
    this.paddingFrameResizeObserver?.disconnect();
    if (!isTestEnv()) {
      layoutManager.setConfig({bannerContainer: null});
    }
    super.disconnectedCallback();
  }

  private renderSidebar(): VNode {
    return (
      <SidenavEl
        sel='.sidenav'
        class={{'hide-nav': this.attr(`hide-nav`)}}
        style={{
          width: this.state.sidenavWidth > 0 ? `${this.state.sidenavWidth}px` : undefined,
          'max-width':
            this.state.maxSidenavWidth > SIDENAV_COLLAPSED_WIDTH ? `${this.state.maxSidenavWidth}px` : undefined,
          'min-width': `${SIDENAV_COLLAPSED_WIDTH}px`,
        }}
        attrs={{
          elref: `sidenav`,
          selected: this.attr(`selected-nav-item`),
          loading: !this.attr(`loaded`),
          header: this.navHeader,
          'min-adjacent-width': this.getMinContentWidth(),
          footer: this.footer,
          'user-id': this.userId,
          'storage-key': this.attr(`storage-key`),
          'fixed-size': this.attr(`is-sidenav-size-fixed`),
        }}
        on={{
          selectItem: this.passThroughEvent,
          headerAction: this.passThroughEvent,
          footerAction: this.passThroughEvent,
          dragDrop: this.passThroughEvent,
          overflowMenuSelect: this.passThroughEvent,
          sidenavResize: (ev: CustomEvent<{width: number}>) => {
            this.update({sidenavWidth: ev.detail.width});
            this.updateLayout();
          },
        }}
        props={{
          sections: this.navSections,
          confirmNavigateFunc: this.confirmNavigateFunc,
        }}
        hook={stickyElemHooks}
      >
        {/* Note: the (`slot` in this.navHeader) prop check is for Typescript type discrimination, during runtime it's redundant */}
        {this.navHeader && `slot` in this.navHeader && this.navHeader.slot && (
          <slot attrs={{name: this.navHeader.slot, slot: this.navHeader.slot}} />
        )}
      </SidenavEl>
    );
  }

  private renderTemplate(): VNode {
    const isDownloadMode = getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None;
    return (
      <div sel='.root'>
        {this.hasRequiredData() && (
          <>
            {this.hasSidenavConfiguration() && this.renderSidebar()}
            <ContentFrame
              sel='.content-area'
              minContentWidth={this.getMinContentWidth()}
              includePadding={!this.attr(`no-padding`)}
              isDownloadMode={isDownloadMode}
              onPaddingFrameInsert={(vnode: VNode) => {
                const paddingFrameEl = vnode.elm as HTMLElement;
                this.paddingFrameResizeObserver = new ResizeObserver(() => {
                  const padding = parseFloat(
                    window.getComputedStyle(paddingFrameEl, null).getPropertyValue(`padding-left`),
                  );
                  this.dispatchEvent(
                    new CustomEvent(`contentFrameResized`, {
                      detail: {
                        padding,
                      },
                    }),
                  );
                });
                this.paddingFrameResizeObserver.observe(paddingFrameEl);
              }}
            >
              <div
                class={{'no-padding': this.attr(`no-padding`)}}
                sel='.banner-container'
                hook={{
                  insert: (vnode: VNode) => {
                    // don't use layoutManager in test env because some test suites aren't setup to mock banner API requests
                    if (!isTestEnv()) {
                      layoutManager.setConfig({
                        bannerContainer: vnode.elm as HTMLElement,
                      });
                    }
                  },
                }}
              />
              <slot />
            </ContentFrame>
          </>
        )}
      </div>
    );
  }

  private getMinContentWidth(): number {
    const isSidenavVisible = this.hasSidenavConfiguration() && !this.attr(`hide-nav`);
    return isSidenavVisible ? MIN_PAGE_WIDTH - this.state.sidenavWidth : MIN_PAGE_WIDTH;
  }

  private passThroughEvent = (ev: CustomEvent): void => {
    ev.stopPropagation();
    this.dispatchEvent(new CustomEvent(ev.type, ev));
  };

  // hasRequiredData() will block all rendering until it is true
  private hasRequiredData(): boolean {
    // if we plan to show a nav, block all rendering on required nav data to avoid layout jiggling
    // where content is noticeably resized as the nav asynchronously loads
    if (this.hasSidenavConfiguration()) {
      return this.userId !== -1;
    }

    return true;
  }

  // hasSidenavConfiguration() will block nav components from being rendered until it is true
  private hasSidenavConfiguration(): boolean {
    return !!this.navHeader || this.navSections.length > 0 || !!this.footer;
  }

  private initRequiredNavData(): void {
    if (!this.hasRequiredData() && !this.hasInitializedRequiredNavData) {
      const cookieUser = userCookieUtils.getUserIdentityFromCookie();
      if (cookieUser) {
        this.userId = cookieUser.id;
        this.update();
      } else {
        // fallback in environments without accessible identity cookie (eg. localhost development, tests)
        useStoreAndFetchOnce(this, userDetailsStore, () => {
          this.userId = userDetailsStore.value.id;
          this.update();
        });
      }
      this.hasInitializedRequiredNavData = true;
    }
  }

  private updateLayout(): void {
    // check isConnected to avoid theoretical scenario in which updateLayout() is debounced and called after disconnection
    if (this.isConnected && this.selfWidth > 0) {
      this.update({
        maxSidenavWidth: this.selfWidth - this.getMinContentWidth(),
      });
    }
  }
}

export const SidenavLayoutEl = defineAndMakeJsxFactory<SidenavLayoutJsxProps>(`mp-sidenav-layout`, SidenavLayout);
export const SidenavLayoutReact = panel2react<SidenavLayoutJsxProps>(`mp-sidenav-layout`, SidenavLayout);
